const DEFAULT_LANGUAGE = 'en-US';
const DEFAULT_RATE = 0.95;
const MAX_CHUNK_CHARACTERS = 180;
const MAX_NATIVE_CHUNK_CHARACTERS = 1800;
const START_TIMEOUT_MS = 4000;
const WEB_BOUNDARY_GRACE_MS = 240;

let activeSession = null;
let sessionSequence = 0;

export class TtsRuntimeError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = 'TtsRuntimeError';
    this.code = code;
    if (cause) this.cause = cause;
  }
}

export const normalizeSpeechText = value => String(value ?? '')
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export function isAssessNowTabletApp(host = typeof window === 'undefined' ? undefined : window) {
  const userAgent = String(host?.navigator?.userAgent || '');
  const bridge = host?.AssessNowTTS;
  return /(?:^|\s)AssessNowTTS\/1(?:\s|$)/i.test(userAgent)
    || Boolean(bridge
      && typeof bridge.speak === 'function'
      && typeof bridge.openSettings === 'function');
}

// Boundary offsets are based on the normalized string passed to the speech
// engine. Preserve the original display tokens while assigning each visible
// token an offset in that normalized string so newlines and repeated spaces do
// not make highlighting drift during long passages.
export function mapDisplayTokensToSpeech(value) {
  const rawTokens = String(value ?? '').match(/(\s+|[^\s]+)/g) || [];
  let speechCursor = 0;
  let rawCursor = 0;
  let hasContent = false;
  let pendingWhitespace = false;
  return rawTokens.map(text => {
    const rawStart = rawCursor;
    rawCursor += text.length;
    const rawEnd = rawCursor;
    const whitespace = /^\s+$/.test(text);
    if (whitespace) {
      if (hasContent) pendingWhitespace = true;
      return { text, whitespace: true, start: speechCursor, end: speechCursor, rawStart, rawEnd };
    }
    if (hasContent && pendingWhitespace) speechCursor += 1;
    const start = speechCursor;
    speechCursor += text.length;
    hasContent = true;
    pendingWhitespace = false;
    return { text, whitespace: false, start, end: speechCursor, rawStart, rawEnd };
  });
}

export function resolveSpeechBoundaryRange(event, value) {
  const text = normalizeSpeechText(value);
  if (!text) return null;
  const rawStart = Number(event?.charIndex);
  const start = Number.isFinite(rawStart)
    ? Math.max(0, Math.min(text.length - 1, Math.trunc(rawStart)))
    : 0;
  const rawLength = Number(event?.charLength);
  if (Number.isFinite(rawLength) && rawLength > 0) {
    return { start, end: Math.min(text.length, start + Math.trunc(rawLength)) };
  }

  const remainder = text.slice(start);
  if (String(event?.name || '').toLowerCase() === 'sentence') {
    const sentenceEnd = remainder.search(/[.!?](?:\s|$)/);
    return {
      start,
      end: sentenceEnd >= 0 ? Math.min(text.length, start + sentenceEnd + 1) : text.length
    };
  }
  const word = remainder.match(/^\S+/)?.[0] || remainder[0] || '';
  return { start, end: Math.min(text.length, start + Math.max(1, word.length)) };
}

const comparableSpeechWord = value => String(value ?? '')
  .normalize('NFKD')
  .toLocaleLowerCase()
  .replace(/[^\p{L}\p{N}]+/gu, '');

export function createSpeechDisplayMap(displayValue, speechValue = displayValue) {
  const displayText = normalizeSpeechText(displayValue);
  const speechText = normalizeSpeechText(speechValue);
  const displayTokens = mapDisplayTokensToSpeech(displayText).filter(token => !token.whitespace);
  const speechTokens = mapDisplayTokensToSpeech(speechText).filter(token => !token.whitespace);
  const speechToDisplay = new Array(speechTokens.length).fill(-1);
  const direct = displayText === speechText;
  if (direct) {
    speechTokens.forEach((_, index) => { speechToDisplay[index] = index; });
    return {
      displayText,
      speechText,
      displayTokens,
      speechTokens,
      speechToDisplay,
      direct: true,
      coverage: speechTokens.length ? 1 : 0
    };
  }

  let displayCursor = 0;
  let comparableCount = 0;
  let matchedCount = 0;
  speechTokens.forEach((speechToken, speechIndex) => {
    const wanted = comparableSpeechWord(speechToken.text);
    if (!wanted) return;
    comparableCount += 1;
    const boundedEnd = Math.min(displayTokens.length, displayCursor + 32);
    let matchIndex = -1;
    for (let index = displayCursor; index < boundedEnd; index += 1) {
      if (comparableSpeechWord(displayTokens[index].text) === wanted) {
        matchIndex = index;
        break;
      }
    }
    if (matchIndex < 0) return;
    speechToDisplay[speechIndex] = matchIndex;
    displayCursor = matchIndex + 1;
    matchedCount += 1;
  });

  return {
    displayText,
    speechText,
    displayTokens,
    speechTokens,
    speechToDisplay,
    direct: false,
    coverage: comparableCount ? matchedCount / comparableCount : 0
  };
}

export function mapSpeechRangeToDisplayRange(range, alignment) {
  if (!range || !alignment) return null;
  if (alignment.direct) {
    const start = Math.max(0, Math.min(alignment.displayText.length, range.start));
    const end = Math.max(start, Math.min(alignment.displayText.length, range.end));
    return end > start ? { start, end } : null;
  }
  const mapped = [];
  alignment.speechTokens.forEach((token, index) => {
    if (token.start >= range.end || token.end <= range.start) return;
    const displayIndex = alignment.speechToDisplay[index];
    if (displayIndex >= 0 && alignment.displayTokens[displayIndex]) {
      mapped.push(alignment.displayTokens[displayIndex]);
    }
  });
  if (!mapped.length) return null;
  return {
    start: Math.min(...mapped.map(token => token.start)),
    end: Math.max(...mapped.map(token => token.end))
  };
}

export function resolveDisplaySpeechBoundaryRange(event, displayValue, speechValue, alignment) {
  const activeAlignment = alignment || createSpeechDisplayMap(displayValue, speechValue);
  const speechRange = resolveSpeechBoundaryRange(event, activeAlignment.speechText);
  return mapSpeechRangeToDisplayRange(speechRange, activeAlignment);
}

export function speechWordRanges(value) {
  return mapDisplayTokensToSpeech(normalizeSpeechText(value))
    .filter(token => !token.whitespace)
    .map(token => ({
      charIndex: token.start,
      charLength: token.end - token.start,
      text: token.text
    }));
}

const preferredBreak = (text, start, tentativeEnd, maximumCharacters) => {
  const minimum = start + Math.floor(maximumCharacters * 0.45);
  const windowText = text.slice(start, tentativeEnd);
  const punctuation = [...windowText.matchAll(/[.!?;:]\s+/g)].at(-1);
  if (punctuation) {
    const index = start + punctuation.index + punctuation[0].length;
    if (index >= minimum) return index;
  }
  const comma = windowText.lastIndexOf(', ');
  if (comma >= 0 && start + comma + 2 >= minimum) return start + comma + 2;
  const whitespace = Math.max(windowText.lastIndexOf(' '), windowText.lastIndexOf('\n'));
  return whitespace >= 0 && start + whitespace + 1 >= minimum
    ? start + whitespace + 1
    : tentativeEnd;
};

export function chunkSpeechText(value, maximumCharacters = MAX_CHUNK_CHARACTERS) {
  const text = normalizeSpeechText(value);
  if (!text) return [];
  const maximum = Number.isInteger(maximumCharacters)
    ? Math.max(80, Math.min(500, maximumCharacters))
    : MAX_CHUNK_CHARACTERS;
  const chunks = [];
  let cursor = 0;
  while (cursor < text.length) {
    while (cursor < text.length && /\s/.test(text[cursor])) cursor += 1;
    if (cursor >= text.length) break;
    let end = Math.min(text.length, cursor + maximum);
    if (end < text.length) {
      end = preferredBreak(text, cursor, end, maximum);
      if (end <= cursor || end > cursor + maximum) end = cursor + maximum;
    }
    const raw = text.slice(cursor, end);
    const leading = raw.length - raw.trimStart().length;
    const chunk = raw.trim();
    if (chunk) chunks.push({ text: chunk, start: cursor + leading });
    cursor = end;
  }
  return chunks;
}

const functionNamed = (target, names) => names.find(name => typeof target?.[name] === 'function');

function resolveNativeBridge(host) {
  const assessNow = host?.AssessNowTTS;
  if (assessNow && typeof assessNow.speak === 'function') {
    return {
      kind: 'assessnow-android',
      label: 'tablet speech service',
      protocol: 'assessnow-v1',
      managedErrors: true,
      isAvailable: () => typeof assessNow.isAvailable === 'function'
        ? assessNow.isAvailable()
        : true,
      getStatus: () => typeof assessNow.getStatus === 'function'
        ? assessNow.getStatus()
        : null,
      speak: (text, options) => assessNow.speak(text, {
        lang: options.lang,
        rate: options.rate,
        pitch: options.pitch,
        volume: options.volume,
        onstart: options.onStart,
        onend: options.onEnd,
        onerror: options.onError,
        onboundary: options.onBoundary
      }),
      stop: () => typeof assessNow.stop === 'function' ? assessNow.stop() : undefined
    };
  }

  const capacitor = host?.Capacitor?.Plugins?.TextToSpeech;
  if (capacitor && typeof capacitor.speak === 'function') {
    return {
      kind: 'capacitor-native',
      label: 'tablet speech service',
      protocol: 'promise-completion',
      speak: (text, options) => capacitor.speak({
        text,
        lang: options.lang,
        rate: options.rate,
        pitch: 1,
        volume: 1,
        category: 'ambient'
      }),
      stop: () => typeof capacitor.stop === 'function' ? capacitor.stop() : undefined,
      completionPromise: true
    };
  }

  const candidates = [
    ['assessnow-android', host?.AssessNowTTS],
    ['android-text-to-speech', host?.AndroidTextToSpeech],
    ['android-tts', host?.AndroidTTS],
    ['android-webview', host?.Android]
  ];
  for (const [kind, bridge] of candidates) {
    const speakName = functionNamed(bridge, ['speakText', 'speakTts', 'speak']);
    if (!speakName) continue;
    const stopName = functionNamed(bridge, ['stopSpeaking', 'stopSpeech', 'stop', 'cancel']);
    return {
      kind,
      label: 'tablet speech service',
      protocol: 'legacy',
      speak: text => bridge[speakName](text),
      stop: () => stopName ? bridge[stopName]() : undefined,
      completionPromise: false
    };
  }
  return null;
}

function resolveWebSpeech(host) {
  const synthesis = host?.speechSynthesis;
  const Utterance = host?.SpeechSynthesisUtterance;
  return synthesis && typeof synthesis.speak === 'function' && typeof Utterance === 'function'
    ? { synthesis, Utterance }
    : null;
}

export function getTtsCapability(host = typeof window === 'undefined' ? undefined : window) {
  const native = resolveNativeBridge(host);
  if (native) {
    let available = true;
    if (native.protocol === 'assessnow-v1') {
      try {
        const result = native.isAvailable?.();
        if (typeof result === 'boolean') available = result;
        else result?.catch?.(() => undefined);
      } catch {
        available = false;
      }
    }
    return {
      available,
      engine: native.kind,
      label: native.label,
      managedErrors: Boolean(native.managedErrors)
    };
  }
  if (resolveWebSpeech(host)) return { available: true, engine: 'web-speech', label: 'browser speech service' };
  return {
    available: false,
    engine: 'unavailable',
    label: 'speech service unavailable',
    message: 'Read aloud is unavailable in this tablet view. Open AssessNow in Chrome or enable the tablet’s Android Text-to-Speech service, then tap the audio button again.'
  };
}

export function observeTtsCapability(callback, host = typeof window === 'undefined' ? undefined : window) {
  if (!host || typeof callback !== 'function') return () => undefined;
  let stopped = false;
  let lastKey = '';
  let attempts = 0;
  let observedBridge = null;
  let bridgeUnsubscribers = [];
  const detachBridge = () => {
    for (const unsubscribe of bridgeUnsubscribers.splice(0)) {
      try { unsubscribe(); } catch { /* capability polling remains available */ }
    }
    observedBridge = null;
  };
  const attachBridge = () => {
    const bridge = host.AssessNowTTS;
    if (bridge === observedBridge) return;
    detachBridge();
    observedBridge = bridge || null;
    if (!bridge || typeof bridge.on !== 'function') return;
    for (const eventName of ['statuschange', 'idle']) {
      try {
        const possibleUnsubscribe = bridge.on(eventName, publish);
        if (typeof possibleUnsubscribe === 'function') bridgeUnsubscribers.push(possibleUnsubscribe);
        else if (typeof bridge.off === 'function') {
          bridgeUnsubscribers.push(() => bridge.off(eventName, publish));
        }
      } catch { /* ready event and polling remain available */ }
    }
  };
  const publish = () => {
    if (stopped) return;
    attachBridge();
    const capability = getTtsCapability(host);
    const key = `${capability.available}:${capability.engine}:${capability.managedErrors === true}`;
    if (key !== lastKey) {
      lastKey = key;
      callback(capability);
    }
  };
  const synthesis = host.speechSynthesis;
  synthesis?.addEventListener?.('voiceschanged', publish);
  host.addEventListener?.('focus', publish);
  host.document?.addEventListener?.('visibilitychange', publish);
  host.addEventListener?.('assessnow-tts-ready', publish);
  // Preserve the Revision 21.154 event spelling for older wrappers.
  host.addEventListener?.('assessnow:tts-ready', publish);
  const poll = setInterval(() => {
    attempts += 1;
    publish();
    if (attempts >= 20 || getTtsCapability(host).available) clearInterval(poll);
  }, 250);
  publish();
  return () => {
    stopped = true;
    clearInterval(poll);
    detachBridge();
    synthesis?.removeEventListener?.('voiceschanged', publish);
    host.removeEventListener?.('focus', publish);
    host.document?.removeEventListener?.('visibilitychange', publish);
    host.removeEventListener?.('assessnow-tts-ready', publish);
    host.removeEventListener?.('assessnow:tts-ready', publish);
  };
}

const selectVoice = (synthesis, language) => {
  let voices = [];
  try { voices = synthesis.getVoices?.() || []; } catch { voices = []; }
  const wanted = String(language || DEFAULT_LANGUAGE).toLowerCase();
  const base = wanted.split('-')[0];
  return [...voices].sort((left, right) => {
    const score = voice => {
      const lang = String(voice?.lang || '').toLowerCase();
      return (lang === wanted ? 16 : lang.startsWith(`${base}-`) || lang === base ? 8 : 0)
        + (voice?.localService ? 4 : 0)
        + (voice?.default ? 2 : 0);
    };
    return score(right) - score(left);
  })[0] || null;
};

const friendlyError = code => {
  if (code === 'not-allowed') return 'The tablet blocked audio. Tap the audio button again, and confirm that media volume is on.';
  if (code === 'audio-busy') return 'The tablet audio service is busy. Stop other audio and tap Read aloud again.';
  if (code === 'language-unavailable' || code === 'voice-unavailable') return 'The tablet has no compatible speech voice enabled. Enable an English Android Text-to-Speech voice and try again.';
  if (code === 'speech-did-not-start') return 'The tablet speech service did not start. Check media volume, enable Android Text-to-Speech, and tap Read aloud again.';
  return 'Read aloud could not start on this device. Check the tablet’s Text-to-Speech and media-volume settings, then try again.';
};

function stopActive(reason = 'interrupted') {
  const current = activeSession;
  if (current) current.stop(reason);
}

export function stopActiveSpeech(
  reason = 'stopped',
  host = typeof window === 'undefined' ? undefined : window
) {
  const hadActiveSession = Boolean(activeSession);
  stopActive(reason);
  if (hadActiveSession) return;
  // Navigation can race a late native callback. Cancel both public tablet
  // surfaces even when React has already released its local session object.
  try {
    const stopped = host?.AssessNowTTS?.stop?.();
    stopped?.catch?.(() => undefined);
  } catch { /* the Web Speech cancellation below remains available */ }
  try { host?.speechSynthesis?.cancel?.(); } catch { /* cancellation is best effort */ }
}

function nativeErrorFrom(value) {
  const code = String(value?.code || value?.error || value?.message || 'native-error');
  return new TtsRuntimeError(code, friendlyError(code), value instanceof Error ? value : undefined);
}

function startAssessNowNativeSpeech({ bridge, text, callbacks, language, rate, pitch, volume }) {
  const chunks = chunkSpeechText(text, MAX_NATIVE_CHUNK_CHARACTERS);
  const id = ++sessionSequence;
  let chunkIndex = 0;
  let chunkSequence = 0;
  let completionTimer = null;
  let finished = false;
  let started = false;

  const clearTimer = () => {
    if (completionTimer) clearTimeout(completionTimer);
    completionTimer = null;
  };
  const finish = (state, error) => {
    if (finished) return;
    finished = true;
    chunkSequence += 1;
    clearTimer();
    if (activeSession?.id === id) activeSession = null;
    if (state === 'error') callbacks.onError?.(error);
    else callbacks.onEnd?.({ reason: state });
  };
  const session = {
    id,
    engine: bridge.kind,
    stop: reason => {
      if (finished) return;
      try {
        const stopped = bridge.stop?.();
        stopped?.catch?.(() => undefined);
      } catch { /* local state still closes deterministically */ }
      finish(reason || 'stopped');
    }
  };
  activeSession = session;

  const speakNext = () => {
    if (finished || activeSession?.id !== id) return;
    const chunk = chunks[chunkIndex];
    if (!chunk) return finish('completed');
    clearTimer();
    const currentSequence = ++chunkSequence;
    let chunkFinished = false;
    const settleChunk = () => {
      if (chunkFinished || finished || activeSession?.id !== id || currentSequence !== chunkSequence) return;
      chunkFinished = true;
      clearTimer();
      chunkIndex += 1;
      speakNext();
    };
    const failChunk = value => {
      if (chunkFinished || finished || currentSequence !== chunkSequence) return;
      chunkFinished = true;
      finish('error', nativeErrorFrom(value));
    };
    const options = {
      lang: language,
      rate,
      pitch,
      volume,
      onStart: detail => {
        if (finished || currentSequence !== chunkSequence) return;
        if (!started) {
          started = true;
          callbacks.onStart?.({ engine: bridge.kind, ...(detail || {}) });
        }
      },
      onEnd: settleChunk,
      onError: failChunk,
      onBoundary: event => {
        if (finished || currentSequence !== chunkSequence) return;
        const localIndex = Number(event?.charIndex || 0);
        const localLength = Number(event?.charLength || 0);
        callbacks.onBoundary?.({
          ...(event || {}),
          name: event?.name || 'word',
          charIndex: chunk.start + (Number.isFinite(localIndex) ? Math.max(0, localIndex) : 0),
          charLength: Number.isFinite(localLength) ? Math.max(0, localLength) : 0
        });
      }
    };
    try {
      // These calls are intentionally made for every user-triggered run rather
      // than cached at page load. A false status does not short-circuit speak:
      // APK 1.8.0 owns the teacher-safe setup message for that failure.
      const availability = bridge.isAvailable?.();
      availability?.catch?.(() => undefined);
      const status = bridge.getStatus?.();
      status?.catch?.(() => undefined);
      const result = bridge.speak(chunk.text, options);
      if (result === false) throw new Error('Native speech service rejected the request');
      if (result && typeof result.then === 'function') {
        result.then(settleChunk).catch(failChunk);
      }
      const words = Math.max(1, chunk.text.split(/\s+/).length);
      completionTimer = setTimeout(
        () => failChunk({ code: 'speech-timeout' }),
        Math.max(15000, Math.min(120000, words * 900 + 10000))
      );
    } catch (error) {
      failChunk(error);
    }
  };

  // Return the session before a synchronous JavaScript bridge can invoke an
  // error/end callback. This keeps React's session reference and visual state
  // consistent even when Android rejects a request immediately.
  if (typeof queueMicrotask === 'function') queueMicrotask(speakNext);
  else Promise.resolve().then(speakNext);
  return session;
}

function startLegacyNativeSpeech({ host, bridge, text, callbacks, language, rate }) {
  let finished = false;
  let completionTimer = null;
  let nativeListener = null;
  const id = ++sessionSequence;
  const finish = (state, error) => {
    if (finished) return;
    finished = true;
    if (completionTimer) clearTimeout(completionTimer);
    if (nativeListener) host.removeEventListener?.('assessnow:tts-native-state', nativeListener);
    if (activeSession?.id === id) activeSession = null;
    if (state === 'error') callbacks.onError?.(error);
    else callbacks.onEnd?.({ reason: state });
  };
  const session = {
    id,
    engine: bridge.kind,
    stop: reason => {
      if (finished) return;
      try { bridge.stop?.(); } catch { /* the state still resets locally */ }
      finish(reason || 'stopped');
    }
  };
  activeSession = session;
  nativeListener = event => {
    const state = event?.detail?.state;
    if (state === 'end' || state === 'stopped') finish(state);
    if (state === 'error') finish('error', new TtsRuntimeError('native-error', friendlyError('native-error')));
  };
  host.addEventListener?.('assessnow:tts-native-state', nativeListener);
  callbacks.onStart?.({ engine: bridge.kind });
  try {
    const result = bridge.speak(text, { lang: language, rate });
    if (result === false) throw new Error('Native speech service rejected the request');
    if (bridge.completionPromise && result && typeof result.then === 'function') {
      result.then(() => finish('completed')).catch(error => finish('error', new TtsRuntimeError('native-error', friendlyError('native-error'), error)));
    } else {
      const words = Math.max(1, text.split(/\s+/).length);
      completionTimer = setTimeout(() => finish('completed'), Math.max(1500, Math.min(120000, words * 430)));
    }
  } catch (error) {
    finish('error', new TtsRuntimeError('native-error', friendlyError('native-error'), error));
  }
  return session;
}

function startWebSpeech({ host, web, text, callbacks, language, rate }) {
  const { synthesis, Utterance } = web;
  const chunks = chunkSpeechText(text);
  const id = ++sessionSequence;
  let chunkIndex = 0;
  let utterance = null;
  let finished = false;
  let startTimer = null;
  let completionTimer = null;
  let retryTimer = null;
  let resumeTimer = null;
  let boundaryGraceTimer = null;
  let boundaryWordTimer = null;
  let chunkBoundaryMode = 'waiting';
  let hasStarted = false;
  let chunkStarted = false;
  let chunkAttempt = 0;
  const voice = selectVoice(synthesis, language);

  const clearBoundaryTimers = () => {
    if (boundaryGraceTimer) clearTimeout(boundaryGraceTimer);
    if (boundaryWordTimer) clearTimeout(boundaryWordTimer);
    boundaryGraceTimer = null;
    boundaryWordTimer = null;
  };
  const clearChunkTimers = () => {
    if (startTimer) clearTimeout(startTimer);
    if (completionTimer) clearTimeout(completionTimer);
    if (retryTimer) clearTimeout(retryTimer);
    clearBoundaryTimers();
    startTimer = null;
    completionTimer = null;
    retryTimer = null;
  };
  const clearTimers = () => {
    clearChunkTimers();
    if (resumeTimer) clearInterval(resumeTimer);
    resumeTimer = null;
  };
  const finish = (state, error) => {
    if (finished) return;
    finished = true;
    clearTimers();
    utterance = null;
    if (activeSession?.id === id) activeSession = null;
    if (state === 'error') callbacks.onError?.(error);
    else callbacks.onEnd?.({ reason: state });
  };
  const session = {
    id,
    engine: 'web-speech',
    stop: reason => {
      if (finished) return;
      finished = true;
      clearTimers();
      try { synthesis.cancel(); } catch { /* reset callbacks below */ }
      utterance = null;
      if (activeSession?.id === id) activeSession = null;
      callbacks.onEnd?.({ reason: reason || 'stopped' });
    }
  };
  activeSession = session;

  const estimatedWordDelay = word => {
    const letters = Math.max(1, [...String(word?.text || '').replace(/[^\p{L}\p{N}]/gu, '')].length);
    const punctuationPause = /[.!?]$/.test(word?.text || '')
      ? 260
      : /[,;:]$/.test(word?.text || '') ? 130 : 0;
    return Math.max(170, Math.min(900, Math.round((190 + letters * 25 + punctuationPause) / rate)));
  };

  const armEstimatedBoundaries = (chunk, expectedChunkIndex) => {
    if (!callbacks.onBoundary || chunkBoundaryMode === 'engine') return;
    clearBoundaryTimers();
    boundaryGraceTimer = setTimeout(() => {
      boundaryGraceTimer = null;
      if (finished || activeSession?.id !== id || chunkIndex !== expectedChunkIndex || chunkBoundaryMode === 'engine') return;
      const words = speechWordRanges(chunk.text);
      if (!words.length) return;
      chunkBoundaryMode = 'estimated';
      const fallbackStartedAt = Date.now();
      let wordIndex = 0;
      const emitWord = () => {
        if (finished || activeSession?.id !== id || chunkIndex !== expectedChunkIndex || chunkBoundaryMode !== 'estimated') return;
        const word = words[wordIndex];
        if (!word) return;
        callbacks.onBoundary?.({
          name: 'word',
          charIndex: chunk.start + word.charIndex,
          charLength: word.charLength,
          elapsedTime: (Date.now() - fallbackStartedAt) / 1000,
          source: 'estimated',
          estimated: true
        });
        wordIndex += 1;
        if (wordIndex < words.length) {
          boundaryWordTimer = setTimeout(emitWord, estimatedWordDelay(word));
        }
      };
      emitWord();
    }, WEB_BOUNDARY_GRACE_MS);
  };

  const speakNext = (retryCurrentChunk = false) => {
    if (finished || activeSession?.id !== id) return;
    const chunk = chunks[chunkIndex];
    if (!chunk) return finish('completed');
    clearChunkTimers();
    chunkStarted = false;
    chunkBoundaryMode = 'waiting';
    if (!retryCurrentChunk) chunkAttempt = 0;
    utterance = new Utterance(chunk.text);
    utterance.lang = voice?.lang || language;
    utterance.rate = rate;
    utterance.pitch = 1;
    utterance.volume = 1;
    if (voice) utterance.voice = voice;
    utterance.onstart = () => {
      if (finished || activeSession?.id !== id) return;
      chunkStarted = true;
      if (!hasStarted) {
        hasStarted = true;
        callbacks.onStart?.({ engine: 'web-speech', voice: voice?.name || '' });
      }
      if (startTimer) clearTimeout(startTimer);
      startTimer = null;
      armEstimatedBoundaries(chunk, chunkIndex);
    };
    utterance.onboundary = event => {
      if (!finished && activeSession?.id === id) {
        chunkStarted = true;
        chunkBoundaryMode = 'engine';
        clearBoundaryTimers();
        callbacks.onBoundary?.({
          ...event,
          charIndex: chunk.start + Number(event.charIndex || 0),
          source: 'engine',
          estimated: false
        });
      }
    };
    utterance.onend = () => {
      if (finished || activeSession?.id !== id) return;
      clearChunkTimers();
      chunkIndex += 1;
      speakNext();
    };
    utterance.onerror = event => {
      if (finished || activeSession?.id !== id) return;
      const code = String(event?.error || 'speech-error');
      clearChunkTimers();
      if ((code === 'canceled' || code === 'interrupted') && chunkAttempt < 1) {
        chunkAttempt += 1;
        try { synthesis.cancel(); } catch { /* retry remains bounded */ }
        retryTimer = setTimeout(() => speakNext(true), 90);
        return;
      }
      finish('error', new TtsRuntimeError(code, friendlyError(code)));
    };
    startTimer = setTimeout(() => {
      if (finished || chunkStarted || synthesis.speaking || synthesis.pending) return;
      try { synthesis.cancel(); } catch { /* continue to controlled error */ }
      finish('error', new TtsRuntimeError('speech-did-not-start', friendlyError('speech-did-not-start')));
    }, START_TIMEOUT_MS);
    const estimatedChunkMs = Math.max(12000, Math.min(45000, chunk.text.split(/\s+/).length * 750 + 8000));
    completionTimer = setTimeout(() => {
      if (finished || activeSession?.id !== id) return;
      try { synthesis.cancel(); } catch { /* continue to controlled error */ }
      finish('error', new TtsRuntimeError('speech-timeout', friendlyError('speech-timeout')));
    }, estimatedChunkMs);
    try {
      // Calling cancel immediately before every utterance can swallow playback
      // in Android Chrome. Only clear a genuinely occupied queue.
      if (synthesis.speaking || synthesis.pending || synthesis.paused) synthesis.cancel();
      synthesis.resume?.();
      synthesis.speak(utterance);
    } catch (error) {
      finish('error', new TtsRuntimeError('speech-error', friendlyError('speech-error'), error));
    }
  };

  resumeTimer = setInterval(() => {
    if (finished || activeSession?.id !== id) return;
    try {
      if (synthesis.paused) synthesis.resume();
    } catch { /* the utterance error/timeout path remains authoritative */ }
  }, 1500);
  speakNext();
  return session;
}

export function startSpeech({
  text,
  host = typeof window === 'undefined' ? undefined : window,
  language = DEFAULT_LANGUAGE,
  rate = DEFAULT_RATE,
  pitch = 1,
  volume = 1,
  onStart,
  onBoundary,
  onEnd,
  onError
} = {}) {
  const normalized = normalizeSpeechText(text);
  if (!normalized) throw new TtsRuntimeError('empty-text', 'There is no readable text in this section.');
  if (!host) throw new TtsRuntimeError('unavailable', getTtsCapability(host).message);
  stopActive('interrupted');
  const callbacks = { onStart, onBoundary, onEnd, onError };
  const safeRate = Number.isFinite(Number(rate)) ? Math.max(0.5, Math.min(1.5, Number(rate))) : DEFAULT_RATE;
  const safePitch = Number.isFinite(Number(pitch)) ? Math.max(0.5, Math.min(2, Number(pitch))) : 1;
  const safeVolume = Number.isFinite(Number(volume)) ? Math.max(0, Math.min(1, Number(volume))) : 1;
  const native = resolveNativeBridge(host);
  if (native?.protocol === 'assessnow-v1') {
    return startAssessNowNativeSpeech({
      bridge: native,
      text: normalized,
      callbacks,
      language,
      rate: safeRate,
      pitch: safePitch,
      volume: safeVolume
    });
  }
  if (native) return startLegacyNativeSpeech({ host, bridge: native, text: normalized, callbacks, language, rate: safeRate });
  const web = resolveWebSpeech(host);
  if (web) return startWebSpeech({ host, web, text: normalized, callbacks, language, rate: safeRate });
  const capability = getTtsCapability(host);
  throw new TtsRuntimeError('unavailable', capability.message);
}

